import { useState, useEffect } from 'react';
import { getSupabaseUserFromStoredAccessToken } from '../lib/supabaseAccessTokenClient';

const STORAGE_KEY = 'password-upgrade-prompt-dismissed';

/**
 * Hook to manage password upgrade prompt visibility
 *
 * Security context:
 * - Part of AUTH-VULN-01 remediation (CVSS 7.5 HIGH)
 * - Detects users with passwords <12 characters
 * - Shows dismissible prompt until password is upgraded
 *
 * Detection strategy:
 * Since Supabase doesn't expose password length directly, we use a heuristic:
 * 1. Users created before policy change date (2026-01-20) are flagged
 * 2. Users who haven't updated password since policy change are flagged
 * 3. Prompt can be dismissed per session (sessionStorage)
 * 4. Prompt reappears on next login until password is actually updated
 *
 * @returns {Object} - { showPrompt, isChecking, dismissPrompt }
 */
export const usePasswordUpgradePrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    checkPasswordStrength();
  }, []);

  const checkPasswordStrength = async () => {
    try {
      // Check if user previously dismissed the prompt (session storage)
      const dismissed = sessionStorage.getItem(STORAGE_KEY);
      if (dismissed === 'true') {
        setShowPrompt(false);
        setIsChecking(false);
        return;
      }

      // Get current user
      const { user, error } = await getSupabaseUserFromStoredAccessToken();
      if (error) {
        throw error;
      }

      if (!user) {
        setShowPrompt(false);
        setIsChecking(false);
        return;
      }

      // Check password metadata (if available via user metadata)
      // Supabase doesn't expose password length directly, so we check:
      // 1. If user was created before our policy change
      // 2. If user hasn't updated password since policy change

      const userCreatedAt = new Date(user.created_at);
      const policyChangeDate = new Date('2026-01-20'); // Deployment date of 12-char policy

      // If user created before policy change, prompt to upgrade
      if (userCreatedAt < policyChangeDate) {
        // Check if user has updated password recently
        // Note: Supabase tracks password_updated_at in user metadata when updatePassword is called
        const lastPasswordUpdate = user.user_metadata?.password_updated_at;

        if (!lastPasswordUpdate || new Date(lastPasswordUpdate) < policyChangeDate) {
          setShowPrompt(true);
        } else {
          setShowPrompt(false);
        }
      } else {
        // User created after policy change, assume password is compliant
        // (backend enforces 12-char minimum as of Task 5)
        setShowPrompt(false);
      }
    } catch (error) {
      console.error('[usePasswordUpgradePrompt] Error checking password strength:', error);
      setShowPrompt(false);
    } finally {
      setIsChecking(false);
    }
  };

  const dismissPrompt = () => {
    sessionStorage.setItem(STORAGE_KEY, 'true');
    setShowPrompt(false);
  };

  return {
    showPrompt,
    isChecking,
    dismissPrompt,
  };
};
